import { parseDbTimestamp } from "@repo/shared";
import { ArrowLeft, FileSignature, Trash2 } from "lucide-react";
import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./signing-status-card.i18n";

export type SigningStatusOrder = {
  id: string;
  status: string;
  signatureMethod: string;
  createdAt: string;
};

type SigningStatusCardProps = {
  /** The pending/signing order, or null when none has been created yet. */
  activeOrder: SigningStatusOrder | null;
  /** Back out of the signing step — only offered when there is no active order. */
  onRevert: () => void;
  /** Delete the active order. The same action also lives further down in the signature section. */
  onDeleteRequest: (orderId: string) => void;
  /** QL-292 — an extra way out of the step (return to draft), rendered next to the step action. */
  extraAction?: ReactNode;
};

/**
 * Action box at the top of the signing step, mirroring the submitted step's
 * "Flow submitted / Unlock for editing" card so the way back out of the step is
 * visible without scrolling past the signature and PDF sections (QL-60).
 */
export function SigningStatusCard({
  activeOrder,
  onRevert,
  onDeleteRequest,
  extraAction,
}: SigningStatusCardProps) {
  const { formatDateTime } = useRegionalFormat();
  const t = useT(T);

  if (!activeOrder) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t.noRequestTitle}</CardTitle>
          <CardDescription>{t.noRequestDescription}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={onRevert}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t.revert}
          </Button>
          {extraAction}
        </CardContent>
      </Card>
    );
  }

  const methodLabel = activeOrder.signatureMethod === "simple" ? t.simpleSignature : t.eidSignature;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileSignature className="h-5 w-5 text-primary" />
          {t.requestTitle}
        </CardTitle>
        <CardDescription>{t.requestDescription}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <Badge variant="secondary">
            {activeOrder.status === "signing" ? t.statusSigning : t.statusPending}
          </Badge>
          <span>{methodLabel}</span>
          <span aria-hidden="true">·</span>
          <span>{t.created(formatDateTime(parseDbTimestamp(activeOrder.createdAt)))}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            className="text-destructive hover:text-destructive"
            onClick={() => onDeleteRequest(activeOrder.id)}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            {t.deleteRequest}
          </Button>
          {extraAction}
        </div>
      </CardContent>
    </Card>
  );
}
